import React, { useState, useEffect, useContext } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  DialogContentText,
  IconButton, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  FormControlLabel, 
  Checkbox,
  TextField,
  Grid,
  Box,
  Chip
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { UsageApi } from "../../utils/api";
import { AppContext } from "../../store/AppContext";

// Validation schema
const userSchema = yup.object().shape({
  users: yup.array().of(
    yup.object().shape({
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
      email: yup.string()
        .required("Email is required")
        .email("Please enter a valid email address"),
      role: yup.string().required(),
      isNew: yup.boolean()
    })
  ),
  emailOnNewReport: yup.boolean()
});

const SettingsDialog = ({ 
  open, 
  onClose, 
  name,
  users, 
  onSave,
  emailOnNewReport = false
}) => {
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, index: null });
  const [editingIndexes, setEditingIndexes] = useState([]);
  const { state } = useContext(AppContext);
  const { reportingCohortId } = state;

  // Initialize form with react-hook-form
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      users: users,
      emailOnNewReport: emailOnNewReport
    }
  });

  // Use field array for dynamic form fields
  const { fields, remove, append } = useFieldArray({
    control,
    name: "users"
  });

  // Reset form when dialog opens or users change
  useEffect(() => {
    if (open) {
      reset({ 
        users,
        emailOnNewReport
      });
      setEditingIndexes([]);
      setDeleteConfirmation({ open: false, index: null });
    }
  }, [users, open, reset, emailOnNewReport]);

  // Handle opening delete confirmation
  const handleOpenDeleteConfirmation = (index) => {
    setDeleteConfirmation({ open: true, index });
  };

  // Handle closing delete confirmation
  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmation({ open: false, index: null });
  };

  // Handle confirming delete
  const handleConfirmDelete = () => {
    if (deleteConfirmation.index !== null) {
      remove(deleteConfirmation.index);
    }
    handleCloseDeleteConfirmation();
  };

  // Handle dialog close
  const handleDialogClose = () => {
    onClose(true);
  };

  // Handle cancel button
  const handleCancel = () => {
    onClose(true);
  };

  // Handle save
  const updateReportSettings = async (data) => {
    try {
      // Call the parent component's onSave function directly
      onSave(name, data.users, data.emailOnNewReport);
    } catch (error) {
      console.error("Error saving report settings:", error);
      // You might want to show an error message to the user here
    }
  };

  // Handle adding new user
  const handleAddNewUser = () => {
    append({ 
      firstName: '',
      lastName: '',
      email: '',
      role: 'viewer',
      isNew: true 
    });
  };

  // Start editing a user
  const handleStartEdit = (index) => {
    setEditingIndexes([...editingIndexes, index]);
  };

  // Cancel editing a user
  const handleCancelEdit = (index) => {
    setEditingIndexes(editingIndexes.filter(i => i !== index));
    reset({ users }); // Reset the form to original data
  };

  const renderUserFields = (field, index) => {
    const isEditing = editingIndexes.includes(index) || field.isNew;
    const isPrimary = field.role === 'primary';
    const isCurrentUser = field.email === state.user?.email; // Check if this is the current user

    if (isEditing) {
      return (
        <>
          <TableCell>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Controller
                  name={`users.${index}.firstName`}
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      required
                      label="First Name"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message}
                      size="small"
                      margin="dense"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`users.${index}.lastName`}
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      required
                      label="Last Name"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message}
                      size="small"
                      margin="dense"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </TableCell>
          <TableCell>
            <Controller
              name={`users.${index}.email`}
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  required
                  label="Email"
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error?.message}
                  size="small"
                  margin="dense"
                />
              )}
            />
          </TableCell>
          <TableCell align="right">
            {field.isNew ? (
              <IconButton onClick={() => handleOpenDeleteConfirmation(index)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton onClick={() => handleCancelEdit(index)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            )}
          </TableCell>
        </>
      );
    }

    return (
      <>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>
              {field.firstName} {field.lastName}
            </Typography>
            {isPrimary && (
              <Chip 
                label="Primary" 
                size="small"
                sx={{ 
                  height: 24,
                  backgroundColor: '#E3F2FD',
                  color: '#1976D2',
                  fontWeight: 500,
                  borderRadius: '4px',
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>{field.email}</TableCell>
        <TableCell align="right">
          {!isCurrentUser && (
            <>
              <IconButton onClick={() => handleStartEdit(index)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => handleOpenDeleteConfirmation(index)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </TableCell>
      </>
    );
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleDialogClose} 
        fullWidth 
        maxWidth="md"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Report Settings</Typography>
            <IconButton onClick={handleDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(updateReportSettings)}>
          <DialogContent dividers>
            <Typography variant="h6" gutterBottom>
              Notifications
            </Typography>

            <Typography variant="body1" gutterBottom color="text.secondary">
              Reports are sent on the first day of the month at 8:00am CST.
            </Typography>

            <FormControlLabel
              control={
                <Controller
                  name="emailOnNewReport"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label="Email users when new report is available"
            />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Users
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow key={field.id}>
                      {renderUserFields(field, index)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button 
              variant="outlined" 
              sx={{ mt: 2, display: 'block', mx: 'auto' }} 
              onClick={handleAddNewUser}
            >
              Add
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} color="primary">Cancel</Button>
            <Button type="submit" color="primary" variant="contained">Save</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.open}
        onClose={handleCloseDeleteConfirmation}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmation} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsDialog; 